"use client";

import React, { useState, useEffect } from "react";
import { useGraphStore } from "@/store/graph-store";
import { deleteNodeAction, deleteEdgeAction } from "@/actions/graph-actions";
import { DetailsTab } from "./tabs/details-tab";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import {
  X,
  FileText,
  Network,
  Settings,
  Trash2,
  AlertTriangle,
  ArrowRight,
  HelpCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";

type TabType = "details" | "relations" | "actions";

export function ContextSidebar() {
  const {
    nodes,
    edges,
    selectedNodeId,
    setSelectedNodeId,
    setNodes,
    setEdges,
  } = useGraphStore();

  const [activeTab, setActiveTab] = useState<TabType>("details");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Auto-reset tab and confirm state when selectedNodeId changes
  useEffect(() => {
    setActiveTab("details");
    setShowDeleteConfirm(false);
  }, [selectedNodeId]);

  // If no node selected, keep it closed or return null
  if (!selectedNodeId) return null;

  // Find selected node in Zustand store
  const selectedNode = nodes.find((n) => n.id === selectedNodeId);

  // If node ID is selected but not in the local store yet
  if (!selectedNode) {
    return (
      <aside className="w-80 border-l border-border bg-card text-foreground flex flex-col h-full select-none">
        <div className="p-4 flex items-center justify-between border-b border-border/60">
          <h3 className="text-sm font-bold text-text-primary">
            Loading Node...
          </h3>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-text-secondary hover:bg-card-muted"
            onClick={() => setSelectedNodeId(null)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex-1 flex items-center justify-center p-4">
          <p className="text-xs text-text-secondary">
            Nodo non trovato localmente.
          </p>
        </div>
      </aside>
    );
  }

  // Relations matching selected node
  const nodeEdges = edges.filter(
    (e) => e.source === selectedNodeId || e.target === selectedNodeId,
  );

  // Delete Node Handler
  const handleDeleteNode = async () => {
    setIsDeleting(true);
    try {
      const res = await deleteNodeAction(selectedNodeId);
      if (res && "error" in res) {
        console.error(res.error);
      } else {
        // Filter node and edges out of Zustand store
        setNodes(nodes.filter((n) => n.id !== selectedNodeId));
        setEdges(
          edges.filter(
            (e) => e.source !== selectedNodeId && e.target !== selectedNodeId,
          ),
        );
        setSelectedNodeId(null);
      }
    } catch (err) {
      console.error("Failed to delete node:", err);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Delete Edge Handler
  const handleDeleteEdge = async (edgeId: string) => {
    try {
      const res = await deleteEdgeAction(edgeId);
      if (res && "error" in res) {
        console.error(res.error);
      } else {
        // Update store
        setEdges(edges.filter((e) => e.id !== edgeId));
      }
    } catch (err) {
      console.error("Failed to delete relation edge:", err);
    }
  };

  const nodeType = (selectedNode.data?.type as string) || "work";
  const nodeLabel = (selectedNode.data?.label as string) || selectedNode.id;

  return (
    <aside className="w-80 border-l border-border bg-card text-foreground flex flex-col h-full shadow-lg relative select-none">
      {/* Sidebar Header */}
      <div className="p-4 flex items-center justify-between border-b border-border/60">
        <div className="min-w-0 pr-2">
          <h3
            className="text-sm font-bold text-text-primary truncate"
            title={nodeLabel}
          >
            {nodeLabel}
          </h3>
          <p className="text-[10px] text-text-secondary font-medium uppercase tracking-wider mt-0.5">
            {nodeType}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-text-secondary hover:bg-card-muted shrink-0"
          onClick={() => setSelectedNodeId(null)}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Tabs list navigation */}
      <div className="flex border-b border-border/40 bg-card-muted/40 text-xs font-semibold px-2">
        <button
          onClick={() => setActiveTab("details")}
          className={cn(
            "flex items-center gap-1.5 px-3 py-2.5 border-b-2 transition-colors cursor-pointer",
            activeTab === "details"
              ? "border-primary-500 text-text-primary font-bold"
              : "border-transparent text-text-secondary hover:text-text-primary",
          )}
        >
          <FileText className="h-3.5 w-3.5" />
          Dettagli
        </button>
        <button
          onClick={() => setActiveTab("relations")}
          className={cn(
            "flex items-center gap-1.5 px-3 py-2.5 border-b-2 transition-colors cursor-pointer",
            activeTab === "relations"
              ? "border-primary-500 text-text-primary font-bold"
              : "border-transparent text-text-secondary hover:text-text-primary",
          )}
        >
          <Network className="h-3.5 w-3.5" />
          Relazioni
          {nodeEdges.length > 0 && (
            <span className="ml-0.5 rounded-full bg-card-muted border border-border px-1.5 py-0.2 text-[9px] text-text-secondary">
              {nodeEdges.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab("actions")}
          className={cn(
            "flex items-center gap-1.5 px-3 py-2.5 border-b-2 transition-colors cursor-pointer",
            activeTab === "actions"
              ? "border-primary-500 text-text-primary font-bold"
              : "border-transparent text-text-secondary hover:text-text-primary",
          )}
        >
          <Settings className="h-3.5 w-3.5" />
          Azioni
        </button>
      </div>

      {/* Scrollable Tab Content Panels */}
      <div className="flex-1 overflow-hidden">
        {/* Tab 1: Details Form */}
        {activeTab === "details" && (
          <DetailsTab
            nodeId={selectedNodeId}
            onClose={() => setSelectedNodeId(null)}
          />
        )}

        {/* Tab 2: Relationships List */}
        {activeTab === "relations" && (
          <div className="h-full p-4 overflow-y-auto space-y-4">
            <div>
              <h4 className="text-xs font-bold text-text-primary">
                Connessioni del Nodo
              </h4>
              <p className="text-[10px] text-text-secondary mt-0.5">
                Tutte le relazioni dirette collegate a questo elemento nel grafo
                aziendale.
              </p>
            </div>

            <div className="space-y-2">
              {nodeEdges.length > 0 ? (
                nodeEdges.map((edge) => {
                  const isSource = edge.source === selectedNodeId;
                  const connectedNodeId = isSource ? edge.target : edge.source;
                  const connectedNode = nodes.find(
                    (n) => n.id === connectedNodeId,
                  );
                  const connectedNodeName =
                    (connectedNode?.data?.label as string) || connectedNodeId;
                  const edgeType = (edge.data?.type as string) || "info";

                  const validVariants = [
                    "error",
                    "success",
                    "strategy",
                    "work",
                    "people",
                    "risk",
                    "info",
                    "finance",
                    "operations",
                    "innovation",
                    "sales",
                    "documents",
                    "default",
                    "neutral",
                    "warning",
                  ];
                  const badgeVariant = validVariants.includes(edgeType)
                    ? (edgeType as any)
                    : "info";

                  return (
                    <div
                      key={edge.id}
                      className="group flex flex-col p-3 rounded-lg border border-border bg-card hover:bg-card-muted/30 transition-all text-xs"
                    >
                      <div className="flex items-center justify-between">
                        <span
                          className="font-semibold text-text-primary truncate max-w-[120px]"
                          title={connectedNodeName}
                        >
                          {connectedNodeName}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 rounded text-text-muted hover:text-error hover:bg-error-soft/30 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                          onClick={() => handleDeleteEdge(edge.id)}
                          title="Elimina questa relazione"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>

                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/30">
                        <div className="flex items-center gap-1 text-[10px] text-text-secondary">
                          <span>{isSource ? "Sorgente" : "Destinatario"}</span>
                          <ArrowRight className="h-3 w-3 text-text-muted" />
                        </div>
                        <Badge
                          variant={badgeVariant}
                          className="text-[9px] uppercase tracking-wider py-0 px-1.5 font-bold scale-90"
                        >
                          {edgeType}
                        </Badge>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 border border-dashed border-border rounded-lg bg-card-muted/20">
                  <Network className="h-8 w-8 text-text-muted/65 mx-auto mb-2" />
                  <p className="text-xs text-text-secondary">
                    Nessuna relazione attiva
                  </p>
                  <p className="text-[10px] text-text-muted mt-1 px-4">
                    Collega i nodi trascinando le frecce tra le porte nel
                    Canvas.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab 3: Actions Menu */}
        {activeTab === "actions" && (
          <div className="h-full p-4 overflow-y-auto space-y-4">
            <div>
              <h4 className="text-xs font-bold text-text-primary font-semibold">
                Azioni Rapide
              </h4>
              <p className="text-[10px] text-text-secondary mt-0.5">
                Strumenti di gestione per l'elemento selezionato.
              </p>
            </div>

            <div className="space-y-3 pt-2">
              {showDeleteConfirm ? (
                <div className="p-3 rounded-lg border border-error/30 bg-error-soft/20 text-xs space-y-3">
                  <div className="flex items-start gap-2 text-error">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 animate-bounce" />
                    <div>
                      <p className="font-bold">Conferma eliminazione</p>
                      <p className="text-[10px] text-text-secondary mt-0.5">
                        Questa azione eliminerà definitivamente il nodo e tutte
                        le sue relazioni associate. Non è possibile annullare
                        l'operazione.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowDeleteConfirm(false)}
                      className="flex-1 text-[10px]"
                      disabled={isDeleting}
                    >
                      Annulla
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleDeleteNode}
                      className="flex-1 text-[10px]"
                      loading={isDeleting}
                    >
                      Elimina
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="destructive"
                  className="w-full justify-start text-xs font-semibold"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Elimina Elemento
                </Button>
              )}

              <div className="rounded-lg border border-border p-3 bg-card-muted/20 text-xs space-y-2">
                <div className="flex items-center gap-1.5 text-text-primary font-bold">
                  <HelpCircle className="h-4 w-4 text-text-muted" />
                  <span>Info di Sistema</span>
                </div>
                <div className="text-[10px] text-text-secondary space-y-1 pt-1 border-t border-border/30">
                  <div className="flex justify-between">
                    <span>Organizzazione:</span>
                    <span className="font-mono">Mock Org</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Posizione Canvas:</span>
                    <span className="font-mono">
                      X: {Math.round(selectedNode.position?.x || 0)}, Y:{" "}
                      {Math.round(selectedNode.position?.y || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Stato Render:</span>
                    <span className="text-success-strong font-semibold">
                      Active
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
